<template>
	<div ref="container" style="width: 100%; height: 100%; position: relative; overflow: hidden;"></div>
</template>

<script>
import EmoteWallWidget from '@/widgets/EmoteWallWidget';

const PREVIEW_EMOJIS = ['😀', '😂', '🔥', '💯', '❤️', '🎉', '👍', '🤣', '😍', '🥳', '✨', '🎊', '😎', '🚀', '🌈'];

function pickRandom(arr) {
	return arr[Math.floor(Math.random() * arr.length)];
}

export default {
	name: 'EmoteWallWidgetPreview',
	props: {
		settings: { type: Object, default: () => ({}) },
		realData: { type: Boolean, default: false },
		// Debug-only, deliberately kept out of `settings` — nobody wants wireframes on
		// their actual live wall, so it never gets saved/sent to the real widget.
		showShapes: { type: Boolean, default: false },
		// This user's own real emotes (from GET emotes/mine), so the preview shows what
		// will actually appear instead of only placeholder emoji. Empty when nothing's
		// been learned yet, in which case the preview just falls back to emoji as before.
		realEmotes: { type: Array, default: () => [] },
		// This user's own gift types (from GET userGiftTypes — the same catalog
		// app/widgets/dono-media lists), spawned as stickers when "Show gifts" is on.
		realGiftTypes: { type: Array, default: () => [] },
	},
	data() {
		return {};
	},
	watch: {
		settings: {
			deep: true,
			handler() { this._applySettings(); },
		},
		realData() { this._destroyWidget(); this._initWidget(); },
		showShapes() { this._applySettings(); },
	},
	mounted() {
		this._initWidget();
	},
	beforeDestroy() {
		this._stopPreview();
		this._destroyWidget();
	},
	methods: {
		async _initWidget() {
			this.widget = new EmoteWallWidget({
				container: this.$refs.container,
				baseUrl: this.$baseUrl,
			});

			await this.widget.init({ settings: this._mergedSettings(), giftTypes: [] });

			if(this.realData) {
				this._previewSubId = this.$socket.subscribe('preview', { reconnect: () => this._destroyWidget() || this._initWidget() });
			} else {
				this._startPreview();
			}
		},
		_destroyWidget() {
			if(this._previewSubId != null) { this.$socket.unsubscribe(this._previewSubId); this._previewSubId = null; }
			if(this.widget) {
				this.widget.destroy();
				this.widget = null;
				if(this.$refs.container) this.$refs.container.innerHTML = '';
			}
		},
		// `debug` is EmoteWallWidget's own existing (previously URL-param-only) gate for
		// the physics wireframe overlay — showShapes just exposes it as a real checkbox.
		_mergedSettings() {
			return { ...this.settings, debug: this.showShapes };
		},
		_applySettings() {
			if(!this.widget) return;
			this.widget.applySettings(this._mergedSettings());
		},
		_startPreview() {
			this._spawnFakeEmotes();
			this.previewIntervalId = setInterval(() => this._spawnFakeEmotes(), 1200 + Math.random() * 1500);
		},
		_stopPreview() {
			if(!this.previewIntervalId) return;
			clearInterval(this.previewIntervalId);
			delete this.previewIntervalId;
		},
		_spawnFakeEmotes() {
			if(!this.widget?.settings) return;
			if(this.widget._active.length > 20) return;
			// Mirror "Member emotes only" so the preview shows what the real wall would.
			const pool = this.settings.memberEmotesOnly
				? this.realEmotes.filter(e => e.is_channel && e.is_members)
				: this.realEmotes;
			// Same icon cascade as app/widgets/dono-media's own table: custom media first,
			// then an admin-uploaded image, then whatever the bot's catalog supplied.
			const giftPool = this.settings.showGifts
				? this.realGiftTypes
					.map(g => g.media?.find(m => m.mime?.[0] === 'image')?.url || g.image?.url || g.image_url)
					.filter(Boolean)
				: [];
			const count = 1 + Math.floor(Math.random() * 4);
			const emotes = Array.from({ length: count }, () => {
				if(giftPool.length > 0 && Math.random() < 0.25)
					return { type: 'sticker', src: pickRandom(giftPool) };
				if(pool.length > 0 && Math.random() < 0.5)
					return { type: 'emote', src: pickRandom(pool).image_url };
				return { emoji: pickRandom(PREVIEW_EMOJIS), type: 'emoji' };
			});
			this.widget.spawnEmotes(emotes);
		},
	},
};
</script>
